import {
  BookOpenIcon,
  Squares2X2Icon,
  ChartBarSquareIcon,
  CalendarDaysIcon,
  TrophyIcon,
  AcademicCapIcon,
  UsersIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { AppStripItemConfig } from "./appStripTypes";

/**
 * App Strip configuration
 *
 * Each item can have:
 * - permissions: Direct permission check (user needs ANY one)
 * - navItemInternalNames: Shows if ANY of these nav items are visible
 * - isCertainPathOnly: Only for CertainPath companies
 *
 * For sections (Resources, Stochastic, Admin), visibility is derived from
 * whether the user can see any nav items in that section.
 *
 * For external links (Scoreboard, Training, Partner Network), we use direct
 * permissions since they don't have child nav items.
 */
export const appStripItems: AppStripItemConfig[] = [
  {
    id: "resources",
    name: "Resources",
    icon: BookOpenIcon,
    defaultRoute: "/hub",
    // No permissions - Resources section items (home, resource_library, news, podcasts, help)
    // are generally accessible; specific items within have their own permissions
    navItemInternalNames: [
      "home",
      "resource_library",
      "news",
      "podcasts",
      "financial_reports",
      "help",
    ],
  },
  {
    id: "stochastic",
    name: "Lead Generation",
    icon: Squares2X2Icon,
    defaultRoute: "/stochastic",
    // Visibility derived from stochastic nav items
    navItemInternalNames: [
      "dashboard",
      "roi_dashboard",
      "prospects",
      "customers",
      "invoices",
      "locations",
      "campaigns",
      "dashboards", // uploads parent
    ],
  },
  {
    id: "dashboards",
    name: "Data Connector",
    icon: ChartBarSquareIcon,
    defaultRoute: "/hub/data-connector",
    // Visibility based on data-connector permission
    navItemInternalNames: ["data_connector"],
    standalone: true,
  },
  {
    id: "events",
    name: "Events",
    icon: CalendarDaysIcon,
    defaultRoute: "/event-registration/events",
    // Visibility derived from event-related nav items
    navItemInternalNames: ["event-directory"],
    isCertainPathOnly: true,
  },
  {
    id: "scoreboard",
    name: "Scoreboard",
    icon: TrophyIcon,
    isExternal: true,
    externalUrl: process.env.REACT_APP_SCOREBOARD_URL || "",
    // Direct permission for external link — any scoreboard permission grants access
    permissions: ["scoreboard", "scoreboard-admin", "scoreboard-coach"],
  },
  {
    id: "training",
    name: "Training",
    icon: AcademicCapIcon,
    isExternal: true,
    externalUrl: process.env.REACT_APP_MEMBER_PORTAL_URL || "",
    // Direct permission for external link — any training-portal permission grants access
    permissions: ["training-portal", "training-portal-admin"],
  },
  {
    id: "partner-network",
    name: "Partner Network",
    icon: UsersIcon,
    isExternal: true,
    externalUrl: process.env.REACT_APP_PARTNER_NETWORK_URL || "",
    // Direct permission for external link — any partner-network permission grants access
    permissions: ["partner-network", "partner-network-admin"],
  },
  {
    id: "admin",
    name: "Admin",
    icon: Cog6ToothIcon,
    defaultRoute: "/admin/events",
    // Visibility derived from admin nav items (both internal admin and company admin)
    navItemInternalNames: [
      // Internal Admin items (CertainPath employees)
      "event-registration-admin",
      "email_management",
      "resource_management",
      "user_management",
      "admin", // stochastic admin section
      // Company Admin items (company admins)
      "company_user_management",
      "company_settings",
    ],
  },
];
